/**
 * Configuración y varios locales (bloque C, fase Finanzas, 14 oct 2026).
 *
 * Reglas puras que comparten la API y la web:
 *  - El país pone moneda, hora, IVA de ventas y nombre del dato fiscal; las
 *    cuentas que no cuadran (México con hora de Madrid, IVA 8,25 %) se
 *    PROPONEN corregir, nunca se migran en silencio.
 *  - «Cómo vendes» y los días que cierra son de cada local, heredando de la
 *    cuenta: un local sin nada propio se comporta como antes.
 *  - Ámbito de locales: lo que se pide (`?locationIds=a,b`) acotado a lo que
 *    la persona puede ver (sus locales, si el dueño se los limitó).
 *  - Borrar la cuenta: 14 días de margen.
 */
import { countryPreset, type CountryPreset } from './currency';
import { allocateSharedExpenses, revenueOf } from './finance';
import { monthOf } from './period';
import { etiquetaTaxId } from './tax';
import {
  effectiveLocationId,
  SHARED_LOCATION_ID,
  type DailyRevenue,
  type ExpenseEntry,
  type Location,
  type SharedCostAllocation,
  type TenantSettings,
} from './types';
import { round } from './units';
import { diasDelMes, estadoDia, porFecha } from './ventas';

type Dia = NonNullable<Parameters<typeof estadoDia>[1]>;
import { COUNTRY_TIMEZONE } from './zonaHoraria';

/* ------------------------------------------------------------------ */
/* El país pone lo demás                                               */
/* ------------------------------------------------------------------ */

export interface DatosDePais {
  label: string;
  currency: string;
  /** Zona horaria principal del país (México tiene varias: es la de la capital). */
  timezone?: string;
  vatPct: number;
  /** IVA, IGV, INC, Sales tax… */
  taxName: string;
  /** RFC, NIT, CUIT, RUT, RUC, NIF… */
  taxIdLabel: string;
  note?: string;
  generico?: boolean;
}

export function datosDePais(country: string | undefined): DatosDePais {
  const preset: CountryPreset = countryPreset(country);
  const c = (country ?? '').trim().toUpperCase();
  return {
    label: preset.label,
    currency: preset.currency,
    timezone: COUNTRY_TIMEZONE[c],
    vatPct: preset.vatPct,
    taxName: preset.taxName,
    taxIdLabel: etiquetaTaxId(c),
    note: preset.note,
    generico: preset.generico,
  };
}

/**
 * Tipos de impuesto de venta que tienen sentido en cada país. Fuera de esta
 * lista, el porcentaje guardado se propone corregir. Sin lista (EE. UU., o
 * países sin preset) no se propone nada: el sales tax cambia por ciudad.
 */
const TIPOS_DE_PAIS: Record<string, number[]> = {
  MX: [16, 8, 0],
  ES: [10, 21, 4, 0],
  CO: [8, 19, 0],
  AR: [21, 10.5, 0],
  CL: [19, 0],
  PE: [18, 10, 0],
};

export type CampoDePais = 'currency' | 'timezone' | 'vatPct';

export interface PropuestaDePais {
  campo: CampoDePais;
  actual: string | number;
  propuesto: string | number;
  /** Frase para el aviso: «Tu hora es la de Madrid y tu restaurante está en México». */
  texto: string;
}

const continente = (zona: string | undefined): string | undefined => zona?.split('/')[0];

/**
 * Lo que no cuadra con el país de la cuenta. Se enseña como aviso en «Tu
 * restaurante» con «Corregir»; nada se cambia sin que el dueño lo toque.
 */
export function incoherenciasDePais(
  tenant: { country?: string; currency?: string; timezone?: string },
  settings: Pick<TenantSettings, 'vatPct'> | undefined,
): PropuestaDePais[] {
  const pais = datosDePais(tenant.country);
  if (pais.generico) return [];
  const out: PropuestaDePais[] = [];
  if (tenant.currency && tenant.currency !== pais.currency) {
    out.push({
      campo: 'currency',
      actual: tenant.currency,
      propuesto: pais.currency,
      texto: `Tus números van en ${tenant.currency} y tu restaurante está en ${pais.label}, que usa ${pais.currency}.`,
    });
  }
  if (tenant.timezone && pais.timezone && continente(tenant.timezone) !== continente(pais.timezone)) {
    out.push({
      campo: 'timezone',
      actual: tenant.timezone,
      propuesto: pais.timezone,
      texto: `Tu hora es la de ${nombreDeZona(tenant.timezone)} y tu restaurante está en ${pais.label}.`,
    });
  }
  const tipos = TIPOS_DE_PAIS[(tenant.country ?? '').toUpperCase()];
  if (settings && tipos && !tipos.some((t) => Math.abs(t - settings.vatPct) < 0.001)) {
    out.push({
      campo: 'vatPct',
      actual: settings.vatPct,
      propuesto: pais.vatPct,
      texto: `El ${pais.taxName} de tus ventas está en ${String(settings.vatPct).replace('.', ',')} % y en ${pais.label} es ${pais.vatPct} %.`,
    });
  }
  return out;
}

const ZONAS_EN_ESPANOL: Record<string, string> = {
  'America/Mexico_City': 'Ciudad de México',
  'America/Bogota': 'Bogotá',
  'America/Argentina/Buenos_Aires': 'Buenos Aires',
  'America/Lima': 'Lima',
  'America/Santiago': 'Santiago',
  'America/New_York': 'Nueva York',
  'America/Los_Angeles': 'Los Ángeles',
  'America/Tijuana': 'Tijuana',
  'America/Cancun': 'Cancún',
  'America/Monterrey': 'Monterrey',
  'Europe/Madrid': 'Madrid',
  'Atlantic/Canary': 'Canarias',
};

/** «Europe/Madrid» → «Madrid»; «America/Mexico_City» → «Ciudad de México». */
export function nombreDeZona(zona: string): string {
  return ZONAS_EN_ESPANOL[zona] ?? (zona.split('/').at(-1) ?? zona).replace(/_/g, ' ');
}

/* ------------------------------------------------------------------ */
/* Lo de cada local                                                    */
/* ------------------------------------------------------------------ */

/** Días que cierra un local: los suyos o, si no tiene, los de la cuenta. */
export function diasCerradosDe(
  settings: Pick<TenantSettings, 'closedWeekdays'> | undefined,
  location?: Pick<Location, 'closedWeekdays'> | null,
): number[] {
  return location?.closedWeekdays ?? settings?.closedWeekdays ?? [];
}

/** Días ISO que abre (1 = lunes … 7 = domingo), a partir de los que cierra (0 = domingo). */
export function diasQueAbre(cerrados: number[]): number[] {
  const iso = new Set(cerrados.map((d) => (d === 0 ? 7 : d)));
  return [1, 2, 3, 4, 5, 6, 7].filter((d) => !iso.has(d));
}

/**
 * Los ajustes con los que vende UN local: los de la cuenta y, si la cuenta
 * vende distinto en cada local («Lo mismo en todos» desmarcado), encima lo
 * que tenga guardado ese local. Los días que cierra van siempre por local.
 */
export function ajustesDeVenta(settings: TenantSettings, location?: Location | null): TenantSettings {
  const cerrados = location?.closedWeekdays ?? settings.closedWeekdays;
  const base: TenantSettings = { ...settings, ...(cerrados ? { closedWeekdays: cerrados } : {}) };
  if (!settings.salesPerLocation || !location?.sales) return base;
  const propio = Object.fromEntries(Object.entries(location.sales).filter(([, v]) => v !== undefined));
  return { ...base, ...propio };
}

/** ¿Pregunta el Panel si cuadran las ventas de este local? («No uso punto de venta» = no). */
export function preguntaCuadre(settings: TenantSettings, location?: Location | null): boolean {
  return !ajustesDeVenta(settings, location).salesCheckOff;
}

/* ------------------------------------------------------------------ */
/* Ámbito: qué locales se miran                                        */
/* ------------------------------------------------------------------ */

const ID_VALIDO = /^[\w-]{1,80}$/;

/**
 * `?locationIds=a,b` (o el viejo `?locationId=a`) → lista de ids. Ausente o
 * vacío = todos. Devuelve `null` si algún id no es válido.
 */
export function leerAmbito(raw: string | undefined | null): string[] | undefined | null {
  const partes = (raw ?? '').split(',').map((p) => p.trim()).filter(Boolean);
  if (!partes.length) return undefined;
  if (partes.some((p) => !ID_VALIDO.test(p))) return null;
  return [...new Set(partes)];
}

/** ¿Entra un registro (por su `locationId`, ausente = principal) en el ámbito? */
export function enAmbito(locationId: string | undefined, ambito: readonly string[] | string | undefined): boolean {
  if (ambito === undefined) return true;
  const id = effectiveLocationId(locationId);
  return typeof ambito === 'string' ? id === ambito : ambito.includes(id);
}

/**
 * Lo que se pide, acotado a lo que la persona ve. `permitidos` ausente o
 * vacío = ve todos. Sin pedir nada, alguien con locales limitados ve solo los
 * suyos. Devuelve [] si pide algo que no le toca (la API contesta 403).
 */
export function acotarAmbito(pedido: string[] | undefined, permitidos: readonly string[] | undefined): string[] | undefined {
  if (!permitidos?.length) return pedido;
  if (!pedido) return [...permitidos];
  return pedido.filter((id) => permitidos.includes(id));
}

/** Locales que ve una persona (ausente o vacío = todos). */
export function localesQueVe<T extends Pick<Location, 'locationId'>>(locations: T[], permitidos: readonly string[] | undefined): T[] {
  if (!permitidos?.length) return locations;
  return locations.filter((l) => permitidos.includes(l.locationId));
}

/**
 * Cuando el ámbito son TODOS los locales activos, la vista es el
 * consolidado (gastos comunes enteros). Si no, cada local con su parte.
 */
export function esTodoElGrupo(ambito: readonly string[] | undefined, activos: readonly string[]): boolean {
  return !ambito || activos.every((id) => ambito.includes(id));
}

/**
 * Gastos de VARIOS locales (no todos): los suyos enteros y, de cada gasto
 * común, la suma de las partes que les tocan. Un gasto común sale una vez
 * (con `allocatedPct` = la suma), no una por local.
 */
export function gastosDeLocales(input: {
  expenses: ExpenseEntry[];
  revenues: DailyRevenue[];
  locationIds: readonly string[];
  activeLocationIds: string[];
  allocation?: SharedCostAllocation;
}): ExpenseEntry[] {
  const { locationIds } = input;
  if (locationIds.length === 1) {
    return allocateSharedExpenses({ ...input, locationId: locationIds[0] });
  }
  const propios = input.expenses.filter(
    (e) => e.locationId !== SHARED_LOCATION_ID && locationIds.includes(effectiveLocationId(e.locationId)),
  );
  const comunes = new Map<string, ExpenseEntry>();
  for (const id of locationIds) {
    const parte = allocateSharedExpenses({ ...input, locationId: id }).filter(
      (e) => (e as ExpenseEntry & { allocatedPct?: number }).allocatedPct !== undefined,
    );
    for (const e of parte) {
      // Un pago repartido en varios meses (bloque R) llega en partes con el
      // mismo entryId: cada parte se suma por su cuenta.
      const clave = e.parteDe ? `${e.entryId}#${e.parteDe.n}` : e.entryId;
      const previo = comunes.get(clave) as (ExpenseEntry & { allocatedPct?: number }) | undefined;
      const actual = e as ExpenseEntry & { allocatedPct?: number };
      comunes.set(
        clave,
        previo
          ? ({
              ...previo,
              netAmount: round(previo.netAmount + actual.netAmount, 2),
              allocatedPct: round((previo.allocatedPct ?? 0) + (actual.allocatedPct ?? 0), 2),
            } as ExpenseEntry)
          : ({ ...actual, locationId: SHARED_LOCATION_ID } as ExpenseEntry),
      );
    }
  }
  return [...propios, ...comunes.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * La lista de Gastos con uno o algunos locales (no todos): a cada gasto común
 * (`grupo`) se le pone `importeDelLocal` = lo que les toca, con el mismo
 * reparto que el Estado de resultados (`gastosDeLocales`). El gasto sigue
 * saliendo entero en la lista; los totales suman su parte.
 */
export function conImporteDelLocal(input: {
  expenses: ExpenseEntry[];
  revenues: DailyRevenue[];
  locationIds: readonly string[] | undefined;
  activeLocationIds: string[];
  allocation?: SharedCostAllocation;
}): ExpenseEntry[] {
  const { locationIds } = input;
  if (!locationIds?.length || (locationIds.length > 1 && esTodoElGrupo(locationIds, input.activeLocationIds))) return input.expenses;
  const comunes = input.expenses.filter((e) => e.locationId === SHARED_LOCATION_ID);
  if (!comunes.length) return input.expenses;
  const partes = new Map(
    gastosDeLocales({ ...input, expenses: comunes, locationIds })
      .map((e) => [e.entryId, e] as const),
  );
  return input.expenses.map((e) => {
    const parte = e.locationId === SHARED_LOCATION_ID ? partes.get(e.entryId) : undefined;
    return parte ? { ...e, importeDelLocal: parte.netAmount, allocatedPct: parte.allocatedPct } : e;
  });
}

/**
 * Moneda común de los locales elegidos, o `null` si mezclan monedas: no se
 * suman pesos con euros («Estos locales usan monedas distintas»).
 */
export function monedaDeLocales(
  locations: Array<Pick<Location, 'locationId' | 'currency'>>,
  ambito: readonly string[] | undefined,
  monedaCuenta: string,
): string | null {
  const elegidos = ambito ? locations.filter((l) => ambito.includes(l.locationId)) : locations;
  const monedas = new Set(elegidos.map((l) => l.currency ?? monedaCuenta));
  if (monedas.size > 1) return null;
  return [...monedas][0] ?? monedaCuenta;
}

/* ------------------------------------------------------------------ */
/* Ventas con varios locales                                           */
/* ------------------------------------------------------------------ */

export interface FaltanDeLocal {
  locationId: string;
  name: string;
  /** Días pasados del mes sin anotar ni cerrados en ese local. */
  dias: string[];
}

/** Días sin anotar de cada local, con los días que cierra cada uno. */
export function diasSinAnotarPorLocal(
  month: string,
  revenues: Array<Dia & { date: string; locationId?: string }>,
  locations: Location[],
  settings: TenantSettings,
  hoy: string,
): FaltanDeLocal[] {
  return locations.map((l) => {
    const mapa = porFecha(revenues.filter((r) => effectiveLocationId(r.locationId) === l.locationId));
    const cerrados = diasCerradosDe(settings, l);
    const dias = diasDelMes(month).filter((d) => estadoDia(d, mapa.get(d), cerrados, hoy) === 'sin_anotar');
    return { locationId: l.locationId, name: l.name, dias };
  });
}

export interface DiaDeVariosLocales {
  /** Locales que no anotaron ese día (y no cerraban). */
  faltan: Array<{ locationId: string; name: string }>;
  /** Locales que cerraron ese día (fijo o suelto). */
  cerraron: Array<{ locationId: string; name: string }>;
  /** Cuántos locales anotaron. */
  anotados: number;
}

/** Cómo está un día visto con varios locales: «Falta Centro», «Norte cerró». */
export function diaDeVariosLocales(
  date: string,
  revenues: Array<Dia & { date: string; locationId?: string }>,
  locations: Location[],
  settings: TenantSettings,
  hoy: string,
): DiaDeVariosLocales {
  const out: DiaDeVariosLocales = { faltan: [], cerraron: [], anotados: 0 };
  for (const l of locations) {
    const dia = porFecha(revenues.filter((r) => r.date === date && effectiveLocationId(r.locationId) === l.locationId)).get(date);
    const estado = estadoDia(date, dia, diasCerradosDe(settings, l), hoy);
    if (estado === 'anotado') out.anotados += 1;
    else if (estado === 'cerrado') out.cerraron.push({ locationId: l.locationId, name: l.name });
    else if (estado === 'sin_anotar') out.faltan.push({ locationId: l.locationId, name: l.name });
  }
  return out;
}

/** «Centro», «Centro y Norte», «Centro, Norte y Playa». */
export function listaDeNombres(nombres: string[]): string {
  if (nombres.length <= 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} y ${nombres.at(-1)}`;
}

/** Lo que dice el botón del selector: «Norte», «Centro + Norte», «Todos los locales (3)». */
export function etiquetaDeLocales(locations: Array<Pick<Location, 'locationId' | 'name'>>, ambito: readonly string[] | undefined): string {
  if (!ambito || ambito.length >= locations.length) return `Todos los locales (${locations.length})`;
  const nombres = locations.filter((l) => ambito.includes(l.locationId)).map((l) => l.name);
  return nombres.length ? nombres.join(' + ') : 'Elige un local';
}

/* ------------------------------------------------------------------ */
/* Gastos de todos los locales: ejemplo del reparto                    */
/* ------------------------------------------------------------------ */

export interface ParteDeLocal {
  locationId: string;
  name: string;
  importe: number;
  /** % del total que le toca. */
  pct: number;
}

/**
 * Cómo se reparte `importe` entre los locales en un mes concreto, con la
 * misma regla que el Estado de resultados (`allocateSharedExpenses`). Sirve
 * para el ejemplo de Configuración: «$3,000 de contador → Centro $1,470…».
 */
export function repartoDeEjemplo(
  importe: number,
  month: string,
  locations: Array<Pick<Location, 'locationId' | 'name'>>,
  revenues: DailyRevenue[],
  allocation?: SharedCostAllocation,
): ParteDeLocal[] {
  const activeLocationIds = locations.map((l) => l.locationId);
  const gasto = {
    entryId: 'ejemplo',
    tenantId: '',
    date: `${month}-15`,
    locationId: SHARED_LOCATION_ID,
    netAmount: importe,
  } as unknown as ExpenseEntry;
  const delMes = revenues.filter((r) => monthOf(r.date) === month);
  return locations.map((l) => {
    const [parte] = allocateSharedExpenses({ expenses: [gasto], revenues: delMes, locationId: l.locationId, activeLocationIds, allocation });
    const pct = (parte as ExpenseEntry & { allocatedPct?: number } | undefined)?.allocatedPct ?? 0;
    return { locationId: l.locationId, name: l.name, importe: parte?.netAmount ?? 0, pct };
  });
}

/** Ventas de cada local en un mes (sin IVA), para enseñar el % de cada uno. */
export function ventasPorLocal(revenues: DailyRevenue[], month: string): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of revenues) {
    if (monthOf(r.date) !== month) continue;
    const id = effectiveLocationId(r.locationId);
    out.set(id, (out.get(id) ?? 0) + revenueOf(r));
  }
  return out;
}

/** ¿Suman 100 los porcentajes fijos? (con medio punto de holgura por redondeos). */
export function sumaCien(shares: Record<string, number> | undefined, ids: readonly string[]): boolean {
  const total = ids.reduce((acc, id) => acc + (shares?.[id] ?? 0), 0);
  return Math.abs(total - 100) < 0.05;
}

/* ------------------------------------------------------------------ */
/* Gastos y compras                                                    */
/* ------------------------------------------------------------------ */

const sinAcentos = (texto: string): string =>
  texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Dirección de facturas propuesta con el nombre del restaurante:
 * «Casa Cortijo» → «casacortijo» (facturas-casacortijo@…). Entre 5 y 26
 * caracteres, solo a-z0-9: así cabe un «-1a2b» si ya está cogida.
 */
export function direccionPropuesta(nombre: string): string | undefined {
  const base = sinAcentos(nombre).replace(/[^a-z0-9]+/g, '').slice(0, 26);
  return base.length >= 5 ? base : undefined;
}

/**
 * ¿Se parece un grupo nuevo a uno que ya existe? «Verduras» y «Frutas y
 * verduras»: una palabra con sustancia (4+ letras) en común, o uno dentro del
 * otro. Devuelve el que ya existe.
 */
export function grupoParecido(nuevo: string, existentes: readonly string[]): string | undefined {
  const n = sinAcentos(nuevo);
  if (!n) return undefined;
  const palabras = (t: string) => new Set(t.split(/[^a-z0-9ñ]+/).filter((p) => p.length >= 4).map((p) => p.replace(/(es|s)$/, '')));
  const mias = palabras(n);
  return existentes.find((e) => {
    const otro = sinAcentos(e);
    if (otro === n) return true;
    if (otro.includes(n) || n.includes(otro)) return true;
    return [...palabras(otro)].some((p) => mias.has(p));
  });
}

/* ------------------------------------------------------------------ */
/* Borrar la cuenta                                                    */
/* ------------------------------------------------------------------ */

/** Días que pasan entre «Borrar mi cuenta» y el borrado de verdad. */
export const DIAS_PARA_BORRAR = 14;

export function fechaDeBorrado(pedidoEn: Date | string): string {
  const d = new Date(pedidoEn);
  return new Date(d.getTime() + DIAS_PARA_BORRAR * 86_400_000).toISOString();
}

/** ¿Ya se puede borrar? Solo si la fecha programada pasó. */
export function tocaBorrar(deletion: { scheduledFor: string } | undefined, ahora: Date = new Date()): boolean {
  if (!deletion?.scheduledFor) return false;
  const cuando = Date.parse(deletion.scheduledFor);
  return Number.isFinite(cuando) && cuando <= ahora.getTime();
}

/** Confirmar escribiendo el nombre del restaurante: sin mayúsculas, acentos ni espacios de más. */
export function confirmaNombre(escrito: string, nombre: string): boolean {
  const limpio = (t: string) => sinAcentos(t).replace(/\s+/g, ' ');
  return limpio(escrito) !== '' && limpio(escrito) === limpio(nombre);
}
